import React, { useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';
import backArrow from '../../assets/global/arrow-left.svg';
import { BottomSheet } from 'react-spring-bottom-sheet';
import DetialsPage from './DetialsPage';
import { useMerchantList } from '../../api/api.calls';
import ShimmerLoader from './Shimmer';
import { ctLogV2 } from '../../utils/cleverTapLogger.utils';
import { callFlutterHandler } from '../../utils/flutterBridge';
import { useIdHash } from '../../hooks/custom.hooks';

function BrandList() {
  const { data: apiResponse } = useMerchantList();
  const listData = apiResponse;
  const [online, setOnline] = useState([]);
  const [offline, setOffline] = useState([]);
  const [isBanner, setIsBanner] = useState(false);
  const [isopen, setIsOpen] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [store, setStore] = useState(null);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);
  const { custId: cid } = useIdHash();

  useEffect(() => {
    if (!listData) return;

    if (listData) {
      const onlineBrands =
        listData?.categories?.find((cat) => cat.type === 'ONLINE')?.brands ||
        [];
      const offlineBrands =
        listData?.categories?.find((cat) => cat.type === 'OFFLINE')?.brands ||
        [];
      setOnline(onlineBrands);
      setOffline(offlineBrands);
      setIsBanner(listData?.banner?.length > 0);

      const bannerImgs = listData?.banner || [];
      const onlineImgs = onlineBrands.map((brand) => brand.logo_square);
      const offlineImgs = offlineBrands.map((brand) => brand.logo_rectangle);

      const allImageUrls = [...bannerImgs, ...onlineImgs, ...offlineImgs];

      const loadImage = (src) =>
        new Promise((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = resolve;
          img.onerror = resolve;
        });

      Promise.all(allImageUrls.map(loadImage)).then(() => {
        setAllImagesLoaded(true);
      });
    }
  }, [listData]);

  const handleBottomSheet = (data, text) => {
    ctLogV2({
      eventName: 'appMerchantListingIconClicked',
      customerId: cid,
      extraData: {
        merchant_name: data?.name,
        category_name: text.toLowerCase(),
      },
    });
    setDetailsData(data);
    setStore(text);
    setIsOpen(true);
  };

  // Carousel Logic
  const currentIndexRef = useRef(0);
  const trackRef = useRef(null);
  const progressBarRef = useRef([]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newIndex = (currentIndexRef.current + 1) % listData?.banner?.length;
      currentIndexRef.current = newIndex;
      scrollToIndex(newIndex);
      updateProgressBar(newIndex);
    }, 4000);

    return () => clearInterval(interval);
  }, [listData?.banner?.length]);

  const scrollToIndex = (index) => {
    if (trackRef.current) {
      const containerWidth = trackRef.current.offsetWidth;
      trackRef.current.scrollTo({
        left: containerWidth * index,
        behavior: 'smooth',
      });
    }
  };

  const updateProgressBar = (index) => {
    progressBarRef.current.forEach((el, i) => {
      if (el) {
        el.classList.toggle(styles.active, i === index);
      }
    });
  };

  const handleScroll = () => {
    if (trackRef.current) {
      const scrollLeft = trackRef.current.scrollLeft;
      const containerWidth = trackRef.current.offsetWidth;
      const index = Math.round(scrollLeft / containerWidth);
      if (index !== currentIndexRef.current) {
        currentIndexRef.current = index;
        updateProgressBar(index);
      }
    }
  };

  useEffect(() => {
    if (isopen) {
      window.history.pushState({ isBottomSheetOpen: true }, '');
    }
    const handlePopState = () => {
      if (isopen) {
        setIsOpen(false);
        setDetailsData(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isopen]);

  if (!listData || !allImagesLoaded) {
    return <ShimmerLoader />;
  }

  return (
    <>
      {listData && (
        <div className={styles.pageContainer}>
          <div className={styles.headerSection}>
            <img
              src={backArrow}
              alt="<"
              // onClick={() =>
              //   // window?.Flutter?.postMessage('backButton')
              //   window.flutter_inappwebview.callHandler(
              //     'Flutter',
              //     JSON.stringify({
              //       action: 'backButton',
              //     }),
              //   )
              // }
              onClick={() => {
                // window.flutter_inappwebview?.callHandler(
                //   'Flutter',
                //   JSON.stringify({
                //     action: 'backButton',
                //   }),
                // );
                callFlutterHandler('backButton');
              }}
            />
            <div className={styles.savingsInfoBox}>
              <div className={styles.heading}>{listData?.title}</div>
              <div className={styles.subHeading}>{listData?.subtitle}</div>
            </div>
          </div>

          <div className={styles.contentScrollSection}>
            {/* Carousel */}
            {isBanner && (
              <div className={styles.carousel_container}>
                <div
                  className={styles.carousel_track}
                  ref={trackRef}
                  onScroll={handleScroll}
                >
                  {listData?.banner?.map((img, index) => (
                    <img
                      key={index}
                      src={img}
                      alt={`Slide ${index + 1}`}
                      className={styles.carousel_image}
                    />
                  ))}
                </div>
              </div>
            )}

            {isBanner && (
              <div className={styles.barSection}>
                <div className={styles.progress_bar}>
                  {listData?.banner?.map((_, index) => (
                    <div
                      key={index}
                      ref={(el) => (progressBarRef.current[index] = el)}
                      className={styles.progress_segment}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Brand Sections */}
            <div
              className={styles.brandSection}
              style={{ marginTop: isBanner ? '20px' : '24px' }}
            >
              <div className={styles.topBrand}>Top brands</div>

              {/* Online */}
              <div className={styles.onlineSection}>
                <div
                  className={styles.heading}
                  style={{ marginBottom: '16px' }}
                >
                  online
                </div>
                <div
                  className={`${styles.onlinebrandListSection} ${
                    online.length <= 6
                      ? styles.singleRowScroll
                      : online.length >= 9
                        ? styles.doubleRowScroll
                        : styles.twoRowNoScroll
                  }`}
                >
                  {online?.map((elem) => (
                    <div
                      key={elem}
                      className={styles.onlineBrandSection}
                      onClick={() => handleBottomSheet(elem, 'ONLINE')}
                    >
                      <img src={elem?.logo_square} alt="elem?.logo_url" />
                      <div className={styles.brandName}>
                        {elem?.name?.length > 9
                          ? `${elem.name.slice(0, 9)}...`
                          : elem?.name}
                      </div>
                      <div className={styles.offers}>{elem?.discount}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Offline */}
              <div className={styles.offlineSection}>
                <div className={styles.heading}>Offline</div>
                <div
                  className={`${styles.onlinebrandListSection} ${
                    offline.length <= 6
                      ? styles.singleRowScroll
                      : offline.length >= 9
                        ? styles.doubleRowScroll
                        : styles.twoRowNoScroll
                  } ${styles.offlinebrandListSection}`}
                >
                  {offline.map((elem) => (
                    <div
                      key={elem}
                      className={styles.offlineBrandSection}
                      onClick={() => handleBottomSheet(elem, 'OFFLINE')}
                    >
                      <div className={styles.imgContianer}>
                        <img src={elem?.logo_rectangle} alt="" />
                      </div>
                      <div className={styles.brandName}>
                        {elem?.name?.length > 9
                          ? `${elem.name.slice(0, 9)}...`
                          : elem?.name}
                      </div>
                      <div className={styles.offers}>{elem?.discount}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <BottomSheet
            open={isopen}
            onDismiss={() => {
              setIsOpen(false);
              setDetailsData(null);
            }}
            header={false}
            initialFocusRef={false}
            // style={{ overflow: 'hidden' }}
          >
            <DetialsPage
              setIsOpen={setIsOpen}
              detailsData={detailsData}
              store={store}
              listData={listData}
              setDetailsData={setDetailsData}
            />
          </BottomSheet>
        </div>
      )}
    </>
  );
}

export default BrandList;
